import { useState } from "react";
import { contextFactory } from "../context/contextFactory";

export type ModalCallbacks = {
  onClose?: () => void;
  onValidate?: () => void;
};

export type ModalProps = {
  callbacks?: ModalCallbacks;
  dynamicContent?: React.ReactNode;
};

export type ModalContext<ModalNames> = {
  visibleModal?: ModalNames;
  modalProps: Record<string, ModalProps>;
  show: (name: ModalNames, props: ModalProps) => void;
  hide: (name: ModalNames) => void;
};

type ModalNames = string;

export const { useCustomContext: useModals, Provider: ModalProvider } =
  contextFactory<ModalContext<ModalNames>>(function modalContext() {
    const [visibleModal, setVisibleModal] = useState<ModalNames | undefined>(
      undefined
    );
    const [modalProps, setModalProps] = useState<Record<string, ModalProps>>(
      {}
    );
    function show(name: ModalNames, props: ModalProps) {
      setModalProps({
        ...modalProps,
        [name]: props,
      });
      setVisibleModal(name);
    }
    function hide(name: ModalNames) {
      if (visibleModal === name) {
        setVisibleModal(undefined);
      }
    }

    return { show, visibleModal, hide, modalProps };
  });

export function useModal(name: ModalNames) {
  const {
    show: localShow,
    hide: localHide,
    visibleModal,
    modalProps,
  } = useModals();
  function show(props: ModalProps = {}) {
    localShow(name, props);
  }
  function hide() {
    localHide(name);
  }
  const isModalVisible = name === visibleModal;
  const props = modalProps[name];

  return { show, hide, isModalVisible, ...props };
}
